/**
 * The client's single source of the caller's Clerk session token.
 *
 * WHY A MODULE-LEVEL HOLDER RATHER THAN A HOOK
 *
 * `api.ts` is plain functions and `liveConnection.ts` is a plain class; neither
 * is a React component, and neither should become one just to read a token.
 * `<ClerkTokenBridge>` (see `AuthGate.tsx`) publishes the accessor here once
 * Clerk is ready, and these two consumers read it.
 *
 * The value is an ACCESSOR, never a token string. Clerk session tokens are
 * short-lived (~60s) and `getToken()` refreshes them transparently; caching a
 * string here would produce a console that works for one minute after sign-in
 * and then 401s, which is exactly the kind of failure that looks like a
 * different bug entirely.
 *
 * WHEN THERE IS NO ACCESSOR
 *
 * `getAuthToken()` returns null, and callers send no credential. That is the
 * correct behaviour in two real cases: before Clerk has loaded, and under the
 * dev-auth bypass, where `backend`'s `getClerkUserId` resolves the caller from
 * `DEV_AUTH_BYPASS_USER_ID` and never looks at the request's credentials at
 * all. The whole test suite runs in the second case, which is why attaching a
 * credential must stay optional rather than required.
 */

type TokenAccessor = () => Promise<string | null>;

let accessor: TokenAccessor | null = null;

/** Publish Clerk's `getToken`. Called by `<ClerkTokenBridge>`. */
export function setAuthTokenAccessor(next: TokenAccessor | null): void {
  accessor = next;
}

/**
 * Whether a token accessor has been published at all — answerable
 * SYNCHRONOUSLY, which is the point.
 *
 * The live connection opens its `EventSource` synchronously, and must keep
 * doing so when there is no credential to wait for: under the dev-auth bypass
 * there is nothing to fetch, and making that path await a promise would change
 * the observable behaviour of a connection that was working, purely to
 * accommodate a case it is not in. Callers use this to stay synchronous when
 * they can and go async only when a token genuinely has to be minted.
 */
export function hasAuthAccessor(): boolean {
  return accessor !== null;
}

/**
 * The caller's current session token, or null when there is none to send.
 *
 * Never throws: a token fetch that fails must degrade to an unauthenticated
 * request (which the server then refuses with a 401 the UI can show) rather
 * than rejecting inside whatever fetch or EventSource setup called it. A
 * thrown error here would surface as a blank view instead of "signed out".
 */
export async function getAuthToken(): Promise<string | null> {
  if (!accessor) return null;
  try {
    return await accessor();
  } catch {
    return null;
  }
}

/**
 * `Authorization` header for a fetch, or `{}` when unauthenticated.
 *
 * Returned as a spreadable object so call sites read
 * `{ ...(await authHeaders()), accept: "application/json" }` and cannot
 * accidentally send `Authorization: Bearer null` — a header this codebase's
 * own `extractToken` would treat as present-but-empty.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

/**
 * Append the token to a URL as `?authToken=`, for `EventSource` only.
 *
 * `EventSource` cannot set request headers — that is a limitation of the API,
 * not a choice — so the token has to travel in the URL. This is NOT a new
 * pattern invented here: `backend/src/middleware/clerk-auth.ts`'s
 * `extractToken` already reads `?authToken=` as a first-class alternative to
 * the `Authorization` header, because the iOS WebView needs the same escape
 * hatch. Reusing that seam means the server side needs no change at all, and
 * there is exactly one place that validates a Clerk JWT.
 *
 * The cost is real and worth stating: a token in a URL can reach access logs
 * and browser history. It is bounded by Clerk's ~60s token lifetime, and the
 * alternatives were worse — a hand-rolled cookie session would add a second
 * way to authenticate (and a second thing to get wrong), and dropping
 * `EventSource` for a fetch stream would cost the automatic reconnect that
 * Plan 4's live path deliberately relies on.
 */
export async function withAuthParam(url: string): Promise<string> {
  const token = await getAuthToken();
  if (!token) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}authToken=${encodeURIComponent(token)}`;
}
